import { z } from "zod";
import { dateTimeSchema, enforceSameTenant, nonEmptyStringSchema, sha256Schema, tenantOwnedShape, } from "./common.js";
import { documentBlockRefSchema, entityRefSchema, evidenceRefSchema, memberRefSchema, connectionRefSchema, } from "./refs.js";
import { entityTypeSchema, identifierTypeSchema, observedStateTypeSchema, providerSchema, relationTypeSchema, sourceKindSchema, } from "./vocabulary.js";
const queryIdentifierSchema = z
    .object({
    identifierType: identifierTypeSchema,
    namespace: nonEmptyStringSchema.max(512),
    normalizedValue: nonEmptyStringSchema.max(2048),
})
    .strict();
const exactIdentifierOperationSchema = z
    .object({
    operation: z.literal("exact_identifier"),
    identifiers: z.array(queryIdentifierSchema).min(1).max(20),
})
    .strict();
const relationTraversalOperationSchema = z
    .object({
    operation: z.literal("relation_traversal"),
    startEntityRefs: z.array(entityRefSchema).min(1).max(50),
    relationTypes: z.array(relationTypeSchema).min(1).max(50),
    direction: z.enum(["outgoing", "incoming", "both"]),
    maxDepth: z.number().int().min(1).max(4),
})
    .strict();
const lexicalSearchOperationSchema = z
    .object({
    operation: z.literal("lexical_search"),
    terms: z.array(nonEmptyStringSchema.max(256)).min(1).max(30),
    languages: z
        .array(z.enum(["de", "fr", "it", "en", "tr"]))
        .min(1)
        .max(5),
    useControlledExpansion: z.boolean(),
    useTrigramFallback: z.boolean(),
})
    .strict();
const timelineOperationSchema = z
    .object({
    operation: z.literal("timeline"),
    entityRefs: z.array(entityRefSchema).min(1).max(50),
    from: dateTimeSchema.nullable(),
    to: dateTimeSchema.nullable(),
})
    .strict();
const evidenceFetchOperationSchema = z
    .object({
    operation: z.literal("evidence_fetch"),
    evidenceRefs: z.array(evidenceRefSchema).max(100),
    documentBlockRefs: z.array(documentBlockRefSchema).max(100),
})
    .strict()
    .refine((value) => value.evidenceRefs.length + value.documentBlockRefs.length > 0, {
    message: "evidence_fetch requires an evidence or document block reference.",
});
const projectionFetchOperationSchema = z
    .object({
    operation: z.literal("projection_fetch"),
    projectionTypes: z
        .array(z.enum([
        "company",
        "customer",
        "project",
        "open_commitments",
        "recent_decisions",
        "upcoming_dates",
    ]))
        .min(1)
        .max(20),
    entityRefs: z.array(entityRefSchema).max(50),
})
    .strict();
const observedStateOperationSchema = z
    .object({
    operation: z.literal("observed_state"),
    stateTypes: z.array(observedStateTypeSchema).min(1).max(20),
    asOf: dateTimeSchema,
})
    .strict();
export const knowledgeQueryOperationSchema = z.discriminatedUnion("operation", [
    exactIdentifierOperationSchema,
    relationTraversalOperationSchema,
    lexicalSearchOperationSchema,
    timelineOperationSchema,
    evidenceFetchOperationSchema,
    projectionFetchOperationSchema,
    observedStateOperationSchema,
]);
export const knowledgeQueryPlanSchema = z
    .object({
    ...tenantOwnedShape,
    version: z.literal(1),
    questionSha256: sha256Schema,
    actorMemberRef: memberRefSchema,
    operations: z.array(knowledgeQueryOperationSchema).min(1).max(20),
    filters: z
        .object({
        authorizationScope: z.literal("authorized_evidence_only"),
        providers: z.array(providerSchema).max(10),
        sourceKinds: z.array(sourceKindSchema).max(20),
        entityTypes: z.array(entityTypeSchema).max(30),
        connectionRefs: z.array(connectionRefSchema).max(50),
        from: dateTimeSchema.nullable(),
        to: dateTimeSchema.nullable(),
    })
        .strict(),
    limits: z
        .object({
        results: z.number().int().min(1).max(100),
        evidenceBlocks: z.number().int().min(1).max(50),
        traversalNodes: z.number().int().min(1).max(1000),
    })
        .strict(),
    sort: z.enum(["relevance", "newest", "oldest"]),
})
    .strict()
    .superRefine((value, context) => {
    const references = [
        { tenantId: value.actorMemberRef.tenantId, path: ["actorMemberRef"] },
        ...value.filters.connectionRefs.map((reference, index) => ({
            tenantId: reference.tenantId,
            path: ["filters", "connectionRefs", index],
        })),
    ];
    value.operations.forEach((operation, operationIndex) => {
        if (operation.operation === "relation_traversal") {
            operation.startEntityRefs.forEach((reference, index) => references.push({
                tenantId: reference.tenantId,
                path: ["operations", operationIndex, "startEntityRefs", index],
            }));
        }
        else if (operation.operation === "timeline" ||
            operation.operation === "projection_fetch") {
            operation.entityRefs.forEach((reference, index) => references.push({
                tenantId: reference.tenantId,
                path: ["operations", operationIndex, "entityRefs", index],
            }));
        }
        else if (operation.operation === "evidence_fetch") {
            operation.evidenceRefs.forEach((reference, index) => references.push({
                tenantId: reference.tenantId,
                path: ["operations", operationIndex, "evidenceRefs", index],
            }));
            operation.documentBlockRefs.forEach((reference, index) => references.push({
                tenantId: reference.tenantId,
                path: ["operations", operationIndex, "documentBlockRefs", index],
            }));
        }
    });
    enforceSameTenant(value.tenantId, references, context);
    if (value.filters.from &&
        value.filters.to &&
        value.filters.from > value.filters.to) {
        context.addIssue({
            code: "custom",
            path: ["filters", "to"],
            message: "Query date range is inverted.",
        });
    }
});
//# sourceMappingURL=query.js.map